import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Text,
  VStack,
  useColorModeValue,
  useToast, // 新增导入
} from "@chakra-ui/react";
import Header from "../components/Header";
import { pxToRem } from "../utils/editor";
import { useNavigate } from "react-router-dom";
import { pricingPlans, priceDesc, freePlanDesc } from "../utils/const";
import { PaymentService } from "../services/paymentService";
import { useTranslation } from "react-i18next";

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast(); // 新增
  const { t } = useTranslation(); // 添加国际化hook

  // 新增状态
  const [isLoading, setIsLoading] = useState(false);
  const [freeTrialStarted, setFreeTrialStarted] = useState(false); // 控制按钮显示
  const [showFreeTrialSection, setShowFreeTrialSection] = useState(true); // 控制免费试用区域显示

  // 新增免费试用函数
  const startFreeTrial = async () => {
    setIsLoading(true);

    try {
      const result = await PaymentService.startFreeTrial();

      if (result?.status === "success") {
        setFreeTrialStarted(true); // 设置为已开始试用

        toast({
          title: "Free Trial Started",
          description: result?.message || "Your free trial has been activated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // 成功后跳转到 /edit 页面
        setTimeout(() => {
          navigate("/edit");
        }, 1000); // 延迟1秒让用户看到成功提示
      } else {
        toast({
          title: "Free Trial Error",
          description:
            result?.message || "Failed to start free trial. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("启动免费试用失败:", error);
      const msg = error?.response?.data?.message;
      toast({
        title: "Free Trial Error",
        description: msg || "Failed to start free trial. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 检查免费试用状态
  const checkTrialStatus = async () => {
    try {
      const result = await PaymentService.checkFreeTrialStatus();

      if (result?.trial_status === "used") {
        setShowFreeTrialSection(false);
      } else if (result?.trial_status === "in_trial") {
        setFreeTrialStarted(true);
      }
    } catch (error) {
      console.error("检查免费试用状态失败:", error);
      // 出错时默认显示免费试用区域
    }
  };

  // 页面加载时检查试用状态
  useEffect(() => {
    checkTrialStatus();
  }, []);

  // 新增处理套餐选择的函数
  const handlePlanSelect = (plan: string) => {
    navigate(`/payment-current?planType=${plan}`);
  };

  const bgColor = useColorModeValue("#ffffff", "gray.800");
  const textColor = useColorModeValue("#333333", "white");
  return (
    <Box
      bg={bgColor}
      h="100%"
      w="100%"
      overflowY={"auto"}
      px={pxToRem(30)}
      pt={0}
    >
      {/* Header */}
      <Box
        px={pxToRem(30)}
        position="absolute"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
      >
        <Header />
      </Box>

      {/* Main Content */}
      <Box
        px={{ base: pxToRem(4), md: pxToRem(8) }}
        py={pxToRem(8)}
        transform="scale(0.9)"
        position={"relative"}
        h={"100vh"}
        display={"flex"}
        alignItems={"center"}
        // top={pxToRem(-40)}
      >
        <Flex
          direction="column"
          maxW={pxToRem(1430)}
          mx="auto"
          px={{ base: pxToRem(4), md: 0 }}
        >
          {/* Top Section */}
          <Box mb={pxToRem(40)}>
            <Flex
              direction={{ base: "column", md: "row" }}
              align="center"
              justify="space-between"
            >
              <Box w={showFreeTrialSection ? pxToRem(463) : "100%"}>
                <Text
                  fontSize={pxToRem(32)}
                  fontWeight="bold"
                  color={textColor}
                  lineHeight={pxToRem(40)}
                  mb={0}
                >
                  From intelligent PDF and CSV reading and analysis to seamless writing, editing, and typesetting, we streamline your journey from ideas to polished results.
                </Text>
              </Box>
              {showFreeTrialSection && (
                <Box
                  bg="url(/static/payment/free-bg.png)"
                  backgroundSize={"100% 100%"}
                  backgroundRepeat={"no-repeat"}
                  borderRadius={pxToRem(20)}
                  p={pxToRem(20)}
                  mt={{ base: pxToRem(20), md: 0 }}
                  w={pxToRem(914)}
                  minH={pxToRem(204)}
                  textAlign={!freeTrialStarted ? "center" : "left"}
                >
                  <Text
                    fontSize={pxToRem(32)}
                    fontWeight="700"
                    mb={pxToRem(16)}
                  >
                    {!freeTrialStarted
                      ? "Start a 7-day Professional Plan free trial"
                      : "Doenba Edit Professional"}
                    {freeTrialStarted ? (
                      <Box
                        display="inline-block"
                        bg="rgba(255, 255, 255, 0.35)"
                        borderRadius={pxToRem(20)}
                        px={pxToRem(24)}
                        py={pxToRem(2)}
                        mb={pxToRem(16)}
                        ml={pxToRem(34)}
                        border={"1px solid #FFF"}
                      >
                        <Text
                          fontSize={pxToRem(16)}
                          fontWeight="600"
                          color="#333"
                          mb={0}
                        >
                          7-day free trial
                        </Text>
                      </Box>
                    ) : null}
                  </Text>

                  <Text fontSize={pxToRem(24)} color={"#333"} mb={pxToRem(16)}>
                    Get full access to all Doenba Edit features.
                  </Text>
                  {!freeTrialStarted ? (
                    <Button
                      bg="#333333"
                      color="white"
                      fontSize={pxToRem(24)}
                      borderRadius={pxToRem(12)}
                      fontWeight="700"
                      px={pxToRem(48)}
                      py={pxToRem(14)}
                      _hover={{
                        bg: "#555555",
                      }}
                      transition="all 0.2s"
                      onClick={startFreeTrial}
                      isLoading={isLoading}
                      loadingText="Starting..."
                    >
                      Start free trial
                    </Button>
                  ) : null}
                </Box>
              )}
            </Flex>
          </Box>

          {/* Plan Features Boxes */}
          <Flex
            gap={pxToRem(24)}
            direction={{ base: "column", md: "row" }}
            align="stretch"
          >
            {/* Free Plan Features Box */}
            <Box
              bg="white"
              border="1px solid"
              borderColor="#E2E8F0"
              borderRadius={pxToRem(20)}
              px={pxToRem(40)}
              py={pxToRem(20)}
              display={"flex"}
              justifyContent={"space-between"}
              boxShadow="0px 4px 10px rgba(0, 0, 0, 0.05)"
              flex="1"
            >
              <Flex justifyContent={"center"} flexDir={"column"} mr={pxToRem(30)}>
                <Text
                  fontSize={pxToRem(28)}
                  fontWeight="700"
                  color={textColor}
                  mb={pxToRem(4)}
                >
                  {t('free-plan-title')}
                </Text>
                <Text
                  fontSize={pxToRem(16)}
                  fontWeight="600"
                  color="#1D8AFF"
                  mb={0}
                >
                  {t('free-plan-subtitle')}
                </Text>
              </Flex>

              <VStack spacing={pxToRem(4)} align="flex-start">
                {freePlanDesc.map((feature, index) => (
                  <Flex key={index} align="flex-start">
                    <Box
                      w={pxToRem(10)}
                      h={pxToRem(10)}
                      bg="#48BB78"
                      borderRadius="50%"
                      mr={pxToRem(20)}
                      mt={pxToRem(8)}
                      flexShrink={0}
                    />
                    <Text fontSize={pxToRem(18)} color={textColor} mb={0}>
                      {t(feature)}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>

            {/* Professional Plan Features Box */}
            <Box
              bg="white"
              border="1px solid"
              borderColor="#E2E8F0"
              borderRadius={pxToRem(20)}
              px={pxToRem(40)}
              py={pxToRem(20)}
              display={"flex"}
              justifyContent={"space-between"}
              boxShadow="0px 4px 10px rgba(0, 0, 0, 0.05)"
              flex="1.4"
            >
              <Flex justifyContent={"center"} flexDir={"column"} mr={pxToRem(30)}>
                <Image
                  src="/static/payment/card-tick.svg"
                  alt="Card tick"
                  mb={pxToRem(10)}
                  w={pxToRem(60)}
                  h={pxToRem(60)}
                />
                <Box>
                  <Text
                    fontSize={pxToRem(28)}
                    fontWeight="700"
                    color={textColor}
                    mb={pxToRem(4)}
                  >
                    Professional Plan
                  </Text>
                  <Text
                    fontSize={pxToRem(16)}
                    fontWeight="600"
                    color="#1D8AFF"
                    mb={0}
                  >
                    {t('pro-plan-subtitle')}
                  </Text>
                </Box>
              </Flex>

              <VStack spacing={pxToRem(4)} align="flex-start">
                {priceDesc.map((feature, index) => (
                  <Flex key={index} align="flex-start">
                    <Box
                      w={pxToRem(10)}
                      h={pxToRem(10)}
                      bg="#1D8AFF"
                      borderRadius="50%"
                      mr={pxToRem(20)}
                      mt={pxToRem(8)}
                      flexShrink={0}
                    />
                    <Text fontSize={pxToRem(18)} color={textColor} mb={0}>
                      {t(feature)}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>
          </Flex>

          {/* Pricing Cards */}
          <Flex
            justify="center"
            gap={pxToRem(42)}
            direction={{ base: "column", lg: "row" }}
            align="stretch"
            mt={pxToRem(25)}
          >
            {/* Pricing Plans */}
            <Flex
              gap={pxToRem(24)}
              direction={{ base: "column", md: "row" }}
              align="stretch"
              flex="2"
            >
              {pricingPlans.map((planData, index) => (
                <Box
                  key={index}
                  bg="white"
                  border="1px solid"
                  borderColor="#E2E8F0"
                  borderRadius={pxToRem(20)}
                  p={pxToRem(24)}
                  flex="1"
                  boxShadow="0px 4px 10px rgba(0, 0, 0, 0.05)"
                  display="flex"
                  flexDirection="column"
                >
                  <VStack align="center" spacing={pxToRem(16)} flex="1">
                    <Flex direction="column" align="center">
                      <Text
                        fontSize={pxToRem(86)}
                        fontWeight="700"
                        color={textColor}
                        mb={0}
                      >
                        ${planData.price}
                      </Text>
                      <Flex justify="flex-end" w="100%">
                        <Text fontSize={pxToRem(16)} color="#666" mb={0}>
                          /{planData.period}
                        </Text>
                      </Flex>
                    </Flex>
                    <Button
                      bg="#1D8AFF"
                      color="white"
                      borderRadius={pxToRem(12)}
                      w={pxToRem(162)}
                      h={pxToRem(53)}
                      fontSize={pxToRem(24)}
                      fontWeight="700"
                      _hover={{
                        bg: "#0066CC",
                      }}
                      transition="all 0.2s"
                      onClick={() => handlePlanSelect(planData.planType)}
                      mt={pxToRem(10)}
                    >
                      Select
                    </Button>
                  </VStack>
                </Box>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default PaymentPage;
